import { NavLink } from "react-router-dom";
import type { ReactNode } from "react";
import {
  ArrowLeftRight, BookOpen, Building2, FileText, FileX, History, House, IdCard, LayoutGrid,
  Settings, UserPlus, Users, type LucideIcon,
} from "lucide-react";
import { CustomerSwitcher } from "./CustomerSwitcher";
import { useChangelogUnseen } from "../docs/changelogSeen";
import { docsEnabled } from "../env";

export type PortalRole = "internal" | "customer_admin" | "customer_user";

interface LayoutProps {
  role: PortalRole;
  clientName: string | null;
  onLogout?: () => void;
  /** Internal only: which customer's context we are in, from the URL. */
  clientId?: string;
  /** Internal only: that customer's display name, once loaded. */
  scopedClientName?: string | null;
  children: ReactNode;
}

const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? "active" : "");

/** A sidebar link with its page icon. The icon draws in currentColor, so it
 *  follows the label's colour (slate, teal when active), and is aria-hidden:
 *  the link's accessible name stays the bare label. */
function NavItem({ to, end, icon: Icon, children }: {
  to: string; end?: boolean; icon: LucideIcon; children: ReactNode;
}) {
  return (
    <NavLink to={to} end={end} className={navClass}>
      <Icon className="nav-icon" size={16} strokeWidth={1.75} aria-hidden="true" />
      <span>{children}</span>
    </NavLink>
  );
}

/** Public docs, for every role, below a divider -- dev only: in prod neither
 *  the item nor its divider renders. The dot marks change-log entries this
 *  browser has not opened yet. */
function DocsItem() {
  if (!docsEnabled()) return null;
  return (
    <>
      <div className="nav-divider" />
      <DocsLink />
    </>
  );
}

function DocsLink() {
  const unseen = useChangelogUnseen();
  return (
    <NavItem to="/docs" icon={BookOpen}>
      Documentation
      {unseen && <span className="nav-dot" aria-label="New changes" />}
    </NavItem>
  );
}

/**
 * One role-aware shell. Phase 1 shipped two separate sidebars (customer vs.
 * a minimal internal one); Phase 2 collapses them, because an internal user
 * now sees the same four customer-scoped pages once a customer is selected.
 * See docs/superpowers/specs/2026-07-28-customer-portal-phase2-design.md §4.
 */
function Sidebar({ role, clientId }: { role: PortalRole; clientId?: string }) {
  const isCustomer = role === "customer_admin" || role === "customer_user";

  if (isCustomer) {
    return (
      <nav>
        <NavItem to="/" end icon={House}>Dashboard</NavItem>
        <NavItem to="/transactions" icon={ArrowLeftRight}>Transactions</NavItem>
        <NavItem to="/reports" icon={FileText}>Reports</NavItem>
        <NavItem to="/rejections" icon={FileX}>Rejections</NavItem>
        {role === "customer_admin" && <NavItem to="/team" icon={Users}>Team</NavItem>}
        <DocsItem />
      </nav>
    );
  }

  const base = clientId ? `/customers/${clientId}` : null;
  return (
    <nav>
      <NavItem to="/" end icon={LayoutGrid}>Overview</NavItem>
      <NavItem to="/customers" end icon={Building2}>Customers</NavItem>
      <NavItem to="/staff" icon={IdCard}>Staff</NavItem>
      <NavItem to="/audit" icon={History}>Audit</NavItem>
      <NavItem to="/onboard" icon={UserPlus}>Onboard Customer</NavItem>
      {/* Hidden rather than disabled when no customer is selected: the
          row-level endpoints hard-400 without a client_id, so offering them
          unselected would offer a guaranteed error. */}
      {base && (
        <>
          <div className="nav-divider" />
          <NavItem to={base} end icon={House}>Dashboard</NavItem>
          <NavItem to={`${base}/transactions`} icon={ArrowLeftRight}>Transactions</NavItem>
          <NavItem to={`${base}/reports`} icon={FileText}>Reports</NavItem>
          <NavItem to={`${base}/rejections`} icon={FileX}>Rejections</NavItem>
          <NavItem to={`${base}/team`} icon={Users}>Team</NavItem>
          <NavItem to={`${base}/settings`} icon={Settings}>Settings</NavItem>
        </>
      )}
      <DocsItem />
    </nav>
  );
}

export function Layout({
  role, clientName, onLogout, clientId, scopedClientName, children,
}: LayoutProps) {
  const isInternal = role === "internal";
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">SCAD<span className="accent">OX</span></div>
        <Sidebar role={role} clientId={clientId} />
      </aside>
      <div className="main-content">
        <header className="topbar">
          <div>{isInternal && <CustomerSwitcher clientId={clientId} />}</div>
          <div className="right">
            {isInternal ? (scopedClientName ?? "Scadox staff") : (clientName ?? "")}
            <button onClick={onLogout}>Logout</button>
          </div>
        </header>
        <div className="page-body">{children}</div>
      </div>
    </div>
  );
}
